// Main App Component
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx';
import { NotesProvider } from './contexts/NotesContext.jsx';
import { ThemeProvider } from './contexts/ThemeContext.jsx';
import { AIProvider } from './contexts/AIContext.jsx';
import { FoldersProvider } from './contexts/FoldersContext.jsx';
import { TagsProvider } from './contexts/TagsContext.jsx';
import { OrganizationProvider } from './contexts/OrganizationContext.jsx';
import Login from './components/Auth/Login.jsx';
import VSCodeAuth from './components/Auth/VSCodeAuth.jsx';
import Dashboard from './components/Layout/Dashboard.jsx';
import SharedNoteViewer from './components/SharedNoteViewer.jsx';

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="loading-page">
                <div className="loading-content">
                    <div className="loading-spinner"></div>
                    <div>Loading...</div>
                </div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Provider order: FoldersProvider -> TagsProvider -> OrganizationProvider -> NotesProvider -> AIProvider
    return (
        <FoldersProvider>
            <TagsProvider>
                <OrganizationProvider>
                    <NotesProvider>
                        <AIProvider>
                            {children}
                        </AIProvider>
                    </NotesProvider>
                </OrganizationProvider>
            </TagsProvider>
        </FoldersProvider>
    );
}

function PublicRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="loading-page">
                <div className="loading-content">
                    <div className="loading-spinner"></div>
                    <div>Loading...</div>
                </div>
            </div>
        );
    }

    if (user) {
        return <Navigate to="/notes" replace />;
    }

    return children;
}

import LandingPage from './components/Landing/LandingPage.jsx';

function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                }
            />
            {/* VS Code Extension Authentication */}
            <Route
                path="/vscode-auth"
                element={<VSCodeAuth />}
            />
            <Route
                path="/s/:shareId"
                element={<SharedNoteViewer />}
            />
            <Route
                path="/notes"
                element={
                    <ProtectedRoute>
                        <Dashboard />
                    </ProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
                <AuthProvider>
                    <AppRoutes />
                </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}
